/**
 * socialUrl — iter878 · Jay: "they type just their handle and it builds
 * the full link for them" — for every platform, using each platform's
 * official profile-URL format and username rules.
 *
 * One shared builder so the editor preview, the saved value and every
 * public surface (FLYT ID bubbles, Network Profile icons) agree.
 *
 *   toSocialUrl('instagram', '@jay.pernell_')  → https://www.instagram.com/jay.pernell_
 *   toSocialUrl('tiktok', 'jaypernell_')        → https://www.tiktok.com/@jaypernell_
 *   toSocialUrl('tiktok', 'https://jaypernell_') → https://www.tiktok.com/@jaypernell_  (repairs old saves)
 *   toSocialUrl('website', 'mysite.com')        → https://mysite.com
 */

// `allowed` strips anything the platform doesn't permit in a username,
// so a stray space or emoji can't produce a dead link.
export const SOCIAL_PLATFORMS = {
  // Letters, numbers, periods, underscores · max 30
  instagram: { domain: 'instagram.com', allowed: /[^a-z0-9._]/gi, max: 30, build: (h) => `https://www.instagram.com/${h}` },
  // Letters, numbers, underscores, periods · max 24 · URL uses @
  tiktok:    { domain: 'tiktok.com',    allowed: /[^a-z0-9._]/gi, max: 24, build: (h) => `https://www.tiktok.com/@${h}` },
  // Letters, numbers, underscores, hyphens, periods · max 30 · URL uses @
  youtube:   { domain: 'youtube.com',   allowed: /[^a-z0-9._-]/gi, max: 30, build: (h) => `https://www.youtube.com/@${h}` },
  // Letters, numbers, underscores · max 15
  twitter:   { domain: 'x.com',         allowed: /[^a-z0-9_]/gi,   max: 15, build: (h) => `https://x.com/${h}` },
  // Letters, numbers, periods
  facebook:  { domain: 'facebook.com',  allowed: /[^a-z0-9.]/gi,   max: 50, build: (h) => `https://www.facebook.com/${h}` },
  // Letters, numbers, hyphens · personal profile vanity URL
  linkedin:  { domain: 'linkedin.com',  allowed: /[^a-z0-9-]/gi,   max: 100, build: (h) => `https://www.linkedin.com/in/${h}` },
  // Letters, numbers, underscores · max 25
  twitch:    { domain: 'twitch.tv',     allowed: /[^a-z0-9_]/gi,   max: 25, build: (h) => `https://www.twitch.tv/${h}` },
};

// Hosts that are also accepted as already-correct pastes for a platform.
const ALT_HOSTS = { twitter: ['twitter.com'], youtube: ['youtu.be'] };

/** Clean a typed handle to what the platform allows (no @, no spaces). */
export const cleanHandle = (key, raw) => {
  const p = SOCIAL_PLATFORMS[key];
  let h = (raw || '').trim().replace(/^@+/, '');
  if (!p) return h;
  // Usernames are case-insensitive on every platform here — lowercase
  // so iOS auto-capitalizing the first letter never shows up in the link.
  h = h.replace(p.allowed, '').toLowerCase();
  return h.slice(0, p.max);
};

/** Turn whatever the user typed (handle, @handle, bare link, full URL) into a working URL. */
export const toSocialUrl = (key, raw) => {
  const s = (raw || '').trim();
  if (!s) return '';
  if (key === 'website' || !SOCIAL_PLATFORMS[key]) {
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s.replace(/^\/+/, '')}`;
  }
  const p = SOCIAL_PLATFORMS[key];
  if (/^https?:\/\//i.test(s)) {
    // Repair links saved before handles were converted — e.g. a bare
    // handle that got stored as "https://jaypernell_" (no real domain).
    const host = s.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    if (!host.includes('.')) {
      const h = cleanHandle(key, host);
      return h ? p.build(h) : '';
    }
    return s;
  }
  // A pasted link without the https:// part ("instagram.com/jay", "youtu.be/x").
  const hosts = [p.domain, ...(ALT_HOSTS[key] || [])];
  const bare = s.replace(/^www\./i, '').toLowerCase();
  if (s.includes('/') || hosts.some((d) => bare.startsWith(d))) {
    return `https://${s.replace(/^\/+/, '')}`;
  }
  // Otherwise it's a handle — periods are legal in many usernames
  // (jay.pernell), so a dot alone never means "domain" here.
  const h = cleanHandle(key, s);
  return h ? p.build(h) : '';
};

/** Short, human display of a URL for previews: drops https:// and www. */
export const displayUrl = (url) => (url || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '');
